import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { AnimatedBackground } from './components/AnimatedBackground';
import { HeroSection } from './components/HeroSection';
import { AudioSampleSection } from './components/AudioSampleSection';
import { BenefitsSection } from './components/BenefitsSection';
import { ProductShowcaseSection } from './components/ProductShowcaseSection';
import { TestimonialSection } from './components/TestimonialSection';
import { ObjectionsSection } from './components/ObjectionsSection';
import { GuaranteeSection } from './components/GuaranteeSection';
import { CTASection } from './components/CTASection';
import { LegalModals } from './components/LegalModals';
import { ShoppingCart, Shield, Zap, CheckCircle } from 'lucide-react';
import { useCurrency } from './hooks/useCurrency';
export function App() {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const {
    formatPrice
  } = useCurrency();
  useEffect(() => {
    // Meta Pixel init
    const fbqScript = document.createElement('script');
    fbqScript.innerHTML = `
      (function() {
        if (window.fbq && window.fbq.loaded) return;

        !function(f,b,e,v,n,t,s){
          if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)
        }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

        fbq('init', '1588595258841964');

        if (!window.__pv_fired__) {
          window.__pv_fired__ = true;
          fbq('track', 'PageView');
        }
      })();
    `;
    document.head.appendChild(fbqScript);
    // Noscript pixel fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1588595258841964&ev=PageView&noscript=1';
    noscript.appendChild(img);
    document.head.appendChild(noscript);
    // Hotmart InitiateCheckout tracking
    const handleClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement).closest('a');
      if (!a) return;
      const href = (a.getAttribute('href') || '').toLowerCase();
      if (href.includes('hotmart.com') || href.includes('pay.hotmart.com') || href.includes('hotm.art')) {
        if (typeof (window as any).fbq === 'function') {
          ;
          (window as any).fbq('track', 'InitiateCheckout', {
            content_name: document.title,
            content_type: 'product'
          });
        }
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
  return <div className="min-h-screen text-white selection:bg-[#D4AF37] selection:text-black overflow-x-hidden relative" data-id="element-0">
      {/* 3D Animated Background */}
      <AnimatedBackground data-id="element-1" />

      {/* Legal Modals */}
      <LegalModals termsOpen={termsOpen} privacyOpen={privacyOpen} onCloseTerms={() => setTermsOpen(false)} onClosePrivacy={() => setPrivacyOpen(false)} data-id="element-2" />

      {/* Main Content */}
      <div className="relative z-10" data-id="element-3">
        <main data-id="element-4">
          <HeroSection data-id="element-5" />
          <AudioSampleSection data-id="element-6" />
          <BenefitsSection data-id="element-7" />
          <ProductShowcaseSection data-id="element-8" />
          <TestimonialSection data-id="element-9" />
          <ObjectionsSection data-id="element-10" />
          <GuaranteeSection data-id="element-11" />
          <CTASection data-id="element-12" />
        </main>

        {/* Order Section */}
        <section id="order-section" className="py-16 md:py-24 relative" data-id="element-13">
          <div className="max-w-4xl mx-auto px-6" data-id="element-14">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="flex flex-col items-center text-center" data-id="element-15">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" data-id="element-16">
                ¿Listo para transformar tu vida?
              </h2>

              <motion.a href="https://pay.hotmart.com/M104286251F?bid=1770346803141" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.03
            }} whileTap={{
              scale: 0.98
            }} className="group inline-flex flex-col items-center justify-center px-12 py-6 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] shadow-[0_8px_30px_rgba(212,175,55,0.4)] hover:shadow-[0_12px_40px_rgba(212,175,55,0.5)] transition-shadow duration-300" data-id="element-17">
                <div className="flex items-center gap-3" data-id="element-18">
                  <ShoppingCart className="w-6 h-6 text-[#0A0A0A]" data-id="element-19" />
                  <span className="text-xl md:text-2xl font-black text-[#0A0A0A]" data-id="element-20">
                    ¡Quiero el Sistema AntiCaos PRO™!
                  </span>
                </div>
                <span className="text-lg text-[#0A0A0A]/50 line-through mt-1" data-id="element-21">
                  {formatPrice(47)}
                </span>
                <span className="text-3xl md:text-4xl font-black text-[#0A0A0A] mt-1" data-id="element-22">
                  {formatPrice(17)}
                </span>
                <span className="text-sm text-[#0A0A0A]/70 mt-1" data-id="element-23">
                  Precio de lanzamiento
                </span>
              </motion.a>
            </motion.div>
          </div>

          {/* Trust badges */}
          <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6" data-id="element-24">
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-25">
              <span className="flex items-center gap-1" data-id="element-26">
                <Shield className="w-3 h-3 text-[#D4AF37]/60" data-id="element-27" />
                Pago seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-28">
                <Zap className="w-3 h-3 text-[#D4AF37]/60" data-id="element-29" />
                Acceso inmediato
              </span>
              <span className="hidden sm:flex items-center gap-1" data-id="element-30">
                <CheckCircle className="w-3 h-3 text-[#D4AF37]/60" data-id="element-31" />
                Garantía 30 días
              </span>
            </div>
          </div>
        </section>

        <footer className="py-8 bg-[#0A0A0A]/80 backdrop-blur-sm border-t border-[#D4AF37]/10" data-id="element-32">
          <div className="max-w-4xl mx-auto px-6" data-id="element-33">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-34">
              <p className="text-gray-600 text-sm" data-id="element-35">
                © {new Date().getFullYear()} Sistema AntiCaos PRO™. Todos los
                derechos reservados.
              </p>

              <div className="flex items-center gap-6" data-id="element-36">
                <button onClick={() => setTermsOpen(true)} className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors" data-id="element-37">
                  Términos y Condiciones
                </button>
                <button onClick={() => setPrivacyOpen(true)} className="text-sm text-gray-500 hover:text-[#D4AF37] transition-colors" data-id="element-38">
                  Política de Privacidad
                </button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>;
}